import React, { useEffect, useRef, useState } from 'react';
import { getSubmission, saveAnnotation, invalidateSubmission, sendToDefectQueue } from '../api/client';


const NUMERIC_GRADES = [10.0, 9.8, 9.6, 9.4, 9.2, 9.0, 8.5, 8.0, 7.5, 7.0, 6.5,
  6.0, 5.5, 5.0, 4.5, 4.0, 3.5, 3.0, 2.5, 2.0, 1.8, 1.5, 1.0, 0.5];

const GRADE_LABELS: Record<number, string> = {
  10.0: 'Gem Mint', 9.8: 'Near Mint/Mint', 9.6: 'Near Mint+',
  9.4: 'Near Mint', 9.2: 'Near Mint-', 9.0: 'Very Fine/Near Mint',
  8.5: 'Very Fine+', 8.0: 'Very Fine', 7.5: 'Very Fine-',
  7.0: 'Fine/Very Fine', 6.5: 'Fine+', 6.0: 'Fine',
  5.5: 'Fine-', 5.0: 'Very Good/Fine', 4.5: 'Very Good+',
  4.0: 'Very Good', 3.5: 'Very Good-', 3.0: 'Good/Very Good',
  2.5: 'Good+', 2.0: 'Good', 1.8: 'Good-', 1.5: 'Fair/Good',
  1.0: 'Fair', 0.5: 'Poor',
};

interface PhotoUrl { key: string; url: string; label: string; }
interface Defect { area: string; description: string; severity: string; }

interface SubmissionData {
  submission_id: string;
  ai_grade: number | null;
  ai_label: string;
  ai_confidence: string;
  ai_summary: string;
  ai_defects: Defect[];
  ai_notes: string;
  user_counter_grade?: number;
  user_grade_guess?: number;
  user_rating?: string;
  photo_urls: PhotoUrl[];
  annotations: any[];
  rekognition: any;
  needs_bbox_annotation?: boolean;
  defect_labels_saved?: boolean;
}

interface Props {
  submissionId: string;
  onSaved: () => void;
  isAdmin?: boolean;
}

export default function SubmissionReview({ submissionId, onSaved, isAdmin = false }: Props) {
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invalidating, setInvalidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [photoIndex, setPhotoIndex] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const [lens, setLens] = useState<{ cx: number; cy: number; lx: number; ly: number; iw: number; ih: number } | null>(null);

  const handlePhotoMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const ir = img.getBoundingClientRect();
    const pr = img.parentElement!.getBoundingClientRect();
    setLens({
      cx: e.clientX - ir.left,
      cy: e.clientY - ir.top,
      lx: e.clientX - pr.left,
      ly: e.clientY - pr.top,
      iw: ir.width,
      ih: ir.height,
    });
  };

  // 'ng' for No Grade, otherwise a numeric grade or ''
  const [expertGrade, setExpertGrade] = useState<number | 'ng' | ''>('');
  const [notes, setNotes] = useState('');
  const [confidence, setConfidence] = useState('high');
  const [sendingToQueue, setSendingToQueue] = useState(false);
  const [sentToQueue, setSentToQueue] = useState(false);
  const [cleanSpine, setCleanSpine] = useState(false);
  const [cleanCorners, setCleanCorners] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const data = await getSubmission(submissionId);
        if (cancelled) return;
        setSubmission(data);
        const prior = data.annotations?.[0];
        if (prior) {
          setExpertGrade(prior.expert_grade === 'ng' ? 'ng' : prior.expert_grade ?? '');
          setNotes(prior.notes ?? '');
          setConfidence(prior.confidence ?? 'high');
        }
      } catch (e: any) {
        if (cancelled) return;
        if (attempt === 0 && e instanceof TypeError) { load(1); return; }
        setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    setPhotoIndex(0);
    load();
    return () => { cancelled = true; };
  }, [submissionId]);

  const photos = submission?.photo_urls ?? [];
  const selectedPhoto = photos[photoIndex] ?? null;

  const prevPhoto = () => setPhotoIndex(i => Math.max(0, i - 1));
  const nextPhoto = () => setPhotoIndex(i => Math.min(photos.length - 1, i + 1));

  const handleSave = async () => {
    if (expertGrade === '') return;
    setSaving(true);
    setError(null);
    try {
      await saveAnnotation(submissionId, {
        expert_grade: expertGrade === 'ng' ? ('ng' as any) : expertGrade as number,
        expert_label: expertGrade === 'ng' ? 'No Grade — Not a Comic' : GRADE_LABELS[expertGrade as number],
        notes,
        confidence,
        clean_spine: cleanSpine || undefined,
        clean_corners: cleanCorners || undefined,
      });
      setSuccessMsg(expertGrade === 'ng' ? 'Saved! Marked as Not a Comic.' : `Saved! Grade ${expertGrade} recorded.`);
      setTimeout(() => { onSaved(); }, 1200);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInvalidate = async () => {
    if (!window.confirm('Mark this submission as INVALID? It will be removed from all queues and excluded from training exports.')) return;
    setInvalidating(true);
    setError(null);
    try {
      await invalidateSubmission(submissionId);
      setSuccessMsg('Marked as invalid. Returning to queue...');
      setTimeout(() => { onSaved(); }, 1200);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setInvalidating(false);
    }
  };

  if (loading) return <div className="loading">Loading submission...</div>;
  if (!submission) return <div className="error-banner">{error ?? 'Not found'}</div>;

  const divergence = submission.user_counter_grade != null && submission.ai_grade != null
    ? Math.abs(submission.user_counter_grade - submission.ai_grade)
    : null;

  return (
    <div className="review-layout">
      {/* Left: photo viewer */}
      <div className="photo-panel">
        <div className="photo-main">
          {selectedPhoto && (
            <img
              ref={imgRef}
              src={selectedPhoto.url}
              alt={selectedPhoto.label}
              className="main-photo"
              onMouseMove={handlePhotoMouseMove}
              onMouseLeave={() => setLens(null)}
            />
          )}
          {selectedPhoto && <div className="photo-label">{selectedPhoto.label}</div>}
          {lens && selectedPhoto && (
            <div
              className="zoom-lens"
              style={{
                left: lens.lx,
                top: lens.ly,
                backgroundImage: `url(${selectedPhoto.url})`,
                backgroundSize: `${lens.iw * 3}px ${lens.ih * 3}px`,
                backgroundPosition: `${-(lens.cx * 3 - 90)}px ${-(lens.cy * 3 - 90)}px`,
              }}
            />
          )}

          {/* Prev / Next arrows */}
          {photos.length > 1 && (
            <>
              <button
                className="photo-nav photo-nav-prev"
                onClick={prevPhoto}
                disabled={photoIndex === 0}
                aria-label="Previous photo"
              >&#8249;</button>
              <button
                className="photo-nav photo-nav-next"
                onClick={nextPhoto}
                disabled={photoIndex === photos.length - 1}
                aria-label="Next photo"
              >&#8250;</button>
              <div className="photo-counter">{photoIndex + 1} / {photos.length}</div>
            </>
          )}
        </div>

        <div className="photo-strip">
          {photos.map((p, i) => (
            <img
              key={p.key}
              src={p.url}
              alt={p.label}
              title={p.label}
              className={`thumb ${i === photoIndex ? 'thumb-active' : ''}`}
              onClick={() => setPhotoIndex(i)}
            />
          ))}
        </div>
      </div>

      {/* Right: grading panel */}
      <div className="grade-panel">
        <h2>{submissionId}</h2>

        <section className="info-section">
          <h3>AI Assessment (v1)</h3>
          <div className="ai-grade-display">
            <span className="big-grade">{submission.ai_grade != null ? submission.ai_grade.toFixed(1) : 'N/A'}</span>
            <span className="big-label">{submission.ai_label}</span>
            {submission.ai_confidence && (
              <span className="confidence-badge">{submission.ai_confidence} confidence</span>
            )}
            {submission.user_grade_guess != null && (
              <div className="user-guess-badge">
                <span className="user-guess-label">User's Guess</span>
                <span className="user-guess-value">{submission.user_grade_guess.toFixed(1)}</span>
              </div>
            )}
          </div>
          <p className="ai-summary">{submission.ai_summary}</p>

          {submission.ai_defects?.length > 0 && (
            <ul className="defect-list">
              {submission.ai_defects.map((d, i) => (
                <li key={i} className={`defect-item severity-${d.severity}`}>
                  <strong>{d.area}</strong>: {d.description}
                  <span className="severity-tag">{d.severity}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {submission.user_counter_grade != null && (
          <section className="info-section">
            <h3>User Feedback</h3>
            <div className={`user-feedback ${divergence && divergence >= 0.5 ? 'diverged' : ''}`}>
              User said: <strong>{submission.user_counter_grade.toFixed(1)}</strong>
              {divergence != null && (
                <span className="divergence-note">
                  {divergence >= 0.5
                    ? ` — ${divergence.toFixed(1)} pt dispute with AI`
                    : ` — agrees within ${divergence.toFixed(1)} pts`}
                </span>
              )}
              {submission.user_rating && <div>Rating: {submission.user_rating}</div>}
            </div>
          </section>
        )}

        <section className="annotation-section">
          <h3>Your Expert Grade</h3>

          <div className="form-row">
            <label>Numerical Grade</label>
            <select
              value={expertGrade}
              onChange={e => {
                const v = e.target.value;
                setExpertGrade(v === '' ? '' : v === 'ng' ? 'ng' : parseFloat(v));
              }}
              className="grade-select"
            >
              <option value="">— Select grade —</option>
              {NUMERIC_GRADES.map(g => (
                <option key={g} value={g}>{g.toFixed(1)} — {GRADE_LABELS[g]}</option>
              ))}
              <option value="ng">NG — No Grade / Not a Comic</option>
            </select>
          </div>

          <div className="form-row">
            <label>Confidence</label>
            <select value={confidence} onChange={e => setConfidence(e.target.value)}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low — needs second opinion</option>
            </select>
          </div>

          <div className="form-row">
            <label>Notes / Reasoning</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="Explain your grade, note key defects, override reasoning..."
              className="notes-textarea"
            />
          </div>

          <div className="form-row clean-toggle-row">
            <label>Clean Spine</label>
            <div className="toggle-switch" onClick={() => setCleanSpine(v => !v)}>
              <input type="checkbox" checked={cleanSpine} onChange={e => setCleanSpine(e.target.checked)} />
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
              <span className="toggle-label">{cleanSpine ? 'Yes' : 'No'}</span>
            </div>
          </div>

          <div className="form-row clean-toggle-row">
            <label>Clean Corners</label>
            <div className="toggle-switch" onClick={() => setCleanCorners(v => !v)}>
              <input type="checkbox" checked={cleanCorners} onChange={e => setCleanCorners(e.target.checked)} />
              <span className="toggle-track">
                <span className="toggle-thumb" />
              </span>
              <span className="toggle-label">{cleanCorners ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {error && <div className="error-banner">{error}</div>}
          {successMsg && <div className="success-banner">{successMsg}</div>}

          <button
            className="save-btn"
            onClick={handleSave}
            disabled={expertGrade === '' || saving}
          >
            {saving ? 'Saving...' : 'Save Annotation'}
          </button>

          {isAdmin && (
            <button
              className="invalidate-btn"
              onClick={handleInvalidate}
              disabled={invalidating}
            >
              {invalidating ? 'Marking...' : 'Mark as Invalid'}
            </button>
          )}
        </section>

        <section className="annotation-section">
          {submission.defect_labels_saved ? (
            <div className="defect-queue-status">✓ Defect classification complete</div>
          ) : submission.needs_bbox_annotation || sentToQueue ? (
            <div className="defect-queue-status">✓ Sent to defect classification queue</div>
          ) : (
            <button
              className="defect-queue-btn"
              onClick={async () => {
                setSendingToQueue(true);
                try {
                  await sendToDefectQueue(submissionId, photoIndex);
                  setSentToQueue(true);
                } catch (e: any) {
                  setError(e.message);
                } finally {
                  setSendingToQueue(false);
                }
              }}
              disabled={sendingToQueue}
            >
              {sendingToQueue ? 'Sending...' : `Send Photo ${photoIndex + 1} to Defect Classification`}
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
